/*******************************************************************************
 * Copyright     : Copyright (c)  2025 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React from "react";

import {
  Input, SearchIcon, CloseIcon, ImportIcon,
  Button, Card, CardContent, CardHeader, Table,
  TableContent, TableHeader, FilterIcon, DensityMostIcon,
  PlusIcon,
  Pagination, StatusBadge,
  ContentLoader
} from "@forge/common";
import { useTranslation } from "react-i18next";

import { CustomizedTableProps } from "../../../models";
import { LOCALE_CONST } from "../../../utils/constants";
import "./styles.scss";
const CustomizedTable: React.FC<CustomizedTableProps> = ({
  title, tableHeaderData, tableContentData,
  showHideIconClick, filterIconClick, searchOnChange,
  searchValue, tableData, onSearchClear, addPortalClick,
  onPageChange, onPageSizeChange, initialPage,
  maxPages, pageSize, totalItems, totalPages, isPaginationEnabled, status, count, slotLeft, isLoading, isError,errorText
}) => {
  const { t } = useTranslation();


  const onSearchChangeFn = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (searchOnChange) {
      searchOnChange(e.target.value);
    }
  };

  const getPaginationInfoLabel = (pageNumber: number, pgSize: number, totItems: number) => {
    // Calculate the starting index for the current page
    const startItem = (pageNumber - 1) * pgSize + 1;

    // Calculate the ending index for the current page, ensuring it does not exceed totalItems
    const endItem = Math.min(startItem + pgSize - 1, totItems);

    return t('numberOfItems', { pageSize: `${startItem} - ${endItem}`, totalRecords: totItems ?? 0 });
  };

  const renderSearchSuffix = () => {
    if (searchValue) {
      return (
        <div className="cursor-pointer" data-testid="search-close-test"
          onClick={onSearchClear}
        >
          <CloseIcon data-testid="search-close" />
        </div>
      );
    } else {
      return <></>;
    }
  };

  const renderTableContent = () => {
    if (tableData && tableData.length > 0 && !isLoading) {
      return (
        <>
          <CardContent x="none" top="none" className={isPaginationEnabled ? "customized-table-remote-connect" : "customized-table-remote-connect-no-pagination"}>
            <Table onCard>
              <TableHeader data={tableHeaderData} />
              <TableContent data={tableContentData} />
            </Table>
          </CardContent>
          <CardContent>
          </CardContent>
        </>
      );
    }
    else if (isLoading) {
      return (
        <CardContent className="h-96 place-items-center grid">
          <ContentLoader />
        </CardContent>
      );
    }
    else if (isError) {
      return (
        <CardContent className="h-96 place-items-center grid">
          <p>{errorText}</p>
        </CardContent>
      );
    }
    else if (tableData && tableData.length === 0) {
      return (
        <CardContent className="h-96 place-items-center grid">
          <p>{t(LOCALE_CONST.noDataAvailForPagination)}</p>
        </CardContent>
      );
    }
    else {
      return (
        <CardContent className="h-96 place-items-center grid">
          <p>{t(LOCALE_CONST.noResultsFound)}</p>
        </CardContent>
      );
    }
  };

  return (
    <>
      <Card className="customized-table-card-remote-connect">
        <CardHeader className={`card-header-title ${slotLeft ? "with-slot-left-padding" : ""}`}
        >
          <div className="header-top">
            <h2 className="custom-title">{title}</h2>
            <div className="slot-right">
              <Input
                type="text"
                data-testid="search-test"
                placeholder={t(LOCALE_CONST.search)}
                id="search"
                onChange={onSearchChangeFn}
                value={searchValue}
                prefix={<SearchIcon strokeWidth={2} />}
                suffix={renderSearchSuffix()}
                size="regular"
              />
              {filterIconClick && (
                <Button
                  aria-label="Settings"
                  data-testid="filter-icon-test"
                  icon={<FilterIcon strokeWidth={2.5} />}
                  variant="tertiary"
                  onClick={filterIconClick}
                />
              )}
              {showHideIconClick && (
                <Button
                  aria-label="show-hide"
                  icon={<DensityMostIcon strokeWidth={2} />}
                  variant="tertiary"
                  className="show-hide-icon"
                  onClick={showHideIconClick}
                  data-testid="column-icon-test"
                />
              )}
              {addPortalClick && (
                <Button
                  aria-label="Add Port"
                  data-testid="add-portal-test"
                  icon={<PlusIcon strokeWidth={2} />}
                  variant="tertiary"
                  onClick={addPortalClick}
                  className="add-portal-icon"
                >
                  {t(LOCALE_CONST.addPort)}
                </Button>
              )}
            </div>
          </div>
          {/* {slotLeft && (
            <div className="slot-left">
              <div className="status-container">
                <StatusBadge className="auth-status" color="green" />
                <p className="status-text">{slotLeft.status}</p>
              </div>

            </div>
          )} */}
        </CardHeader>
        {slotLeft && <div className="device-count-container">
          <p className="device-count-text">
            {typeof slotLeft?.count === "number" ? slotLeft.count : null} {t(LOCALE_CONST.devices)}
          </p>
        </div>}
        {renderTableContent()}
        {isPaginationEnabled && <CardContent>
          {totalItems > 0 && totalPages > 0 && (
            <Pagination
              currentPage={initialPage}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
              maxPages={maxPages}
              pageSize={pageSize}
              pageSizeOptions={[10, 15, 20, 50, 100]}
              totalItems={totalItems}
              totalPages={totalPages}
              showPageGoTo
              showPageInfo
              showPageSizeList
              pageGoToLabel={t(LOCALE_CONST.goToPage)}
              pageSizeListLabel={t(LOCALE_CONST.rowsPerPage)}
              pageInfoLabel={getPaginationInfoLabel(initialPage, pageSize, (Math.ceil(
                      totalItems / pageSize
              ) ?? 0))}
            />
          )}
        </CardContent>}
      </Card>
    </>
  );
};

export default CustomizedTable;